import { useLang } from '../i18n/context';
import SEO from '../components/SEO';
import { Container, Reveal } from '../components/Reveal';
import { WhatsAppButton, BookButton } from '../components/CTAs';
import { breadcrumbSchema } from '../lib/schema';
import { DIGITAL } from '../data/digital';

export default function DigitalDentistry() {
    const { t, lang } = useLang();
    const c = DIGITAL[lang] || DIGITAL.en;
    return (
        <>
            <SEO
                title={c.metaTitle}
                description={c.metaDesc}
                path="/digital-dentistry"
                lang={lang}
                jsonLd={[
                    breadcrumbSchema(lang, [
                        { name: t.nav.home, path: '/' },
                        { name: t.nav.digitalLink.title, path: '/digital-dentistry' },
                    ]),
                ]}
            />
            <section className="relative overflow-hidden pt-28 md:pt-36" data-testid="digital-hero">
                <div className="pointer-events-none absolute -right-32 -top-20 h-96 w-96 rounded-full bg-peri-200/70" aria-hidden="true" />
                <Container className="relative">
                    <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
                        <Reveal>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gold-700">{c.kicker}</p>
                            <h1 className="mt-4 font-serif text-4xl font-semibold leading-tight tracking-tight text-ink md:text-5xl lg:text-6xl">{c.title}</h1>
                            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink-muted">{c.heroText}</p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <img src="/img/tr-digital.jpg" alt={c.title} className="aspect-[4/3] w-full rounded-3xl border border-peri-200 object-cover" data-testid="digital-hero-img" />
                        </Reveal>
                    </div>
                </Container>
            </section>

            <section className="py-16 md:py-24" data-testid="digital-sections">
                <Container>
                    <div className="grid gap-4 md:grid-cols-2">
                        {c.sections.map((s, i) => (
                            <Reveal key={s.h} delay={i * 0.06} className="h-full">
                                <div className="h-full rounded-3xl border border-peri-200 bg-white p-8 md:p-9">
                                    <span className="font-serif text-3xl italic text-peri-300">{String(i + 1).padStart(2, '0')}</span>
                                    <h2 className="mt-4 font-serif text-xl font-semibold text-ink md:text-2xl">{s.h}</h2>
                                    <p className="mt-3 text-[15px] leading-relaxed text-ink-muted">{s.p}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </Container>
            </section>

            <section className="bg-peri-50 py-16 md:py-24" data-testid="digital-journey">
                <Container>
                    <Reveal>
                        <h2 className="font-serif text-3xl font-semibold text-ink md:text-4xl">{c.journeyTitle}</h2>
                    </Reveal>
                    <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {c.journey.map((j, i) => (
                            <Reveal key={j.t} delay={i * 0.05} className="h-full">
                                <div className="h-full rounded-2xl bg-white p-6">
                                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-700">{String(i + 1).padStart(2, '0')}</span>
                                    <h3 className="mt-3 font-serif text-lg font-semibold text-ink">{j.t}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-ink-muted">{j.d}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.15}>
                        <p className="mt-8 max-w-3xl text-xs italic leading-relaxed text-ink-subtle">{c.note}</p>
                    </Reveal>
                </Container>
            </section>

            <section className="py-16 md:py-24" data-testid="digital-cta-section">
                <Container>
                    <Reveal>
                        <div className="rounded-3xl border border-peri-300 bg-gradient-to-r from-peri-100 to-peri-50 p-8 md:p-12">
                            <div className="grid items-center gap-8 lg:grid-cols-[1fr_auto]">
                                <div>
                                    <h2 className="font-serif text-3xl font-semibold text-ink md:text-4xl">{c.cta}</h2>
                                    <p className="mt-4 max-w-2xl text-base leading-relaxed text-ink-muted">{t.treatLabels.ctaText}</p>
                                </div>
                                <div className="flex flex-col gap-3">
                                    <WhatsAppButton label={t.cta.whatsappLong} source="digital-page" testId="digital-whatsapp-cta" />
                                    <BookButton label={c.ctaBook} lang={lang} source="digital-page" />
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </Container>
            </section>
        </>
    );
}
